import { ChangeDetectionStrategy, Component, effect, inject, signal, untracked } from '@angular/core';
import { DashboardPageStore } from './dashboard-page.store';
import {
    DashboardGridsterItem,
    basicGridsterItem,
    gridsterOptions,
    Safe,
    smallBasicGridsterItem,
    tightBasicGridsterItem,
} from '../../../../shared/constants/dashboard-settings';
import { Gridster, GridsterItem } from 'angular-gridster2';
import { SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY } from '@libs/types/src/services/types/folder-dashboard-settings.type';
import { ServicesDashboardWidgetsEnum } from '@libs/types/src/services/enum/services-dashboard-widgets.enum';
import { FilterBuilder } from '../../../helper/filter.builder';
import {
    prepareDataToSave,
    prepareWidgetDataToView,
    removeDashboardItemsFromLayout,
} from '../../../../../utils/dashboard.utils';
import {
    DashboardSettingsGridObject,
    DashboardSettingsObject,
} from '@libs/types/src/services/object-type/dashboard-settings.object';
import { TranslateService } from '@ngx-translate/core';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { WidgetComponent } from '../../../components/person/widget/widget.component';
import { LowMedicationWidgetComponent } from '../widgets/low-medication-widget/low-medication-widget.component';
import {
    WorkSanctionDashboardWidgetComponent,
} from '../widgets/work-sanction-dashboard-widget/work-sanction-dashboard-widget.component';
import {
    WorkSuggestionDashboardWidgetComponent,
} from '../widgets/work-suggestion-dashboard-widget/work-suggestion-dashboard-widget.component';
import { CelebrationsWidgetComponent } from '../widgets/celebrations-widget/celebrations-widget.component';
import { NearEndVouchersWidgetComponent } from '../widgets/near-end-vouchers-widget/near-end-vouchers-widget.component';
import { RiskyClientsWidgetComponent } from '../widgets/risky-clients-widget/risky-clients-widget.component';
import {
    WorkMovementRestrictionWidgetComponent,
} from '../widgets/work-movement-restriction-widget/work-movement-restriction-widget.component';
import { MailRecordsWidgetComponent } from '../widgets/mail-records-widget/mail-records-widget.component';
import { featureOn } from '../../../../../feature-flag';
import {
    SocialServiceIndividualPlanWidgetComponent,
} from '../../../../../components/social-service/social-service-individual-plan-widget/social-service-individual-plan-widget.component';
import {
    SocialServiceUserTasksWidgetComponent,
} from '../../../../../components/social-service/social-service-user-tasks-widget/social-service-user-tasks-widget.component';
import { ErrorsInFolderWidgetComponent } from '../widgets/errors-in-folder-widget/errors-in-folder-widget.component';
import {
    ClientsWithoutRecentWorkWidgetComponent,
} from '../widgets/clients-without-recent-work-widget/clients-without-recent-work-widget.component';
import { PlannedWorkWidgetComponent } from '../widgets/planned-work-widget/planned-work-widget.component';
import { DebtorsWidgetComponent } from '../widgets/debtors-widget/debtors-widget.component';
import {
    UserMessagesWidgetComponent,
} from '../widgets/user-messages-widget/user-messages-widget.component';
import { WorkServiceAnnouncementDashboardWidgetComponent } from '../widgets/work-service-announcement-dashboard-widget/work-service-announcement-dashboard-widget.component';
import {
    NursingAllowanceDurationWidgetComponent,
} from '../widgets/nursing-allowance-duration-widget/nursing-allowance-duration-widget.component';
import {
    NursingAllowanceConfirmationWidgetComponent,
} from '../widgets/nursing-allowance-confirmation-widget/nursing-allowance-confirmation-widget.component';
import {
OperationalReportsWidgetComponent,
} from '../widgets/operational-reports-widget/operational-reports-widget.component';
import {
    FinancialBalanceWidgetComponent,
} from '../widgets/financial-balance-widget/financial-balance-widget.component';
import { PerformedWorkWidgetComponent } from '../widgets/performed-work-widget/performed-work-widget.component';
import { WorkedHoursWidgetComponent } from '../widgets/worked-hours-widget/worked-hours-widget.component';
import {
    WorkersWorkedHoursWidgetComponent,
} from '../widgets/workers-worked-hours-widget/workers-worked-hours-widget.component';

@Component({
    selector: 'app-dashboard-page',
    templateUrl: './dashboard-page.component.html',
    styleUrl: './dashboard-page.component.scss',
    providers: [DashboardPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        Gridster,
        GridsterItem,
        WidgetComponent,
        ErrorsInFolderWidgetComponent,
        ClientsWithoutRecentWorkWidgetComponent,
        PlannedWorkWidgetComponent,
        DebtorsWidgetComponent,
        OperationalReportsWidgetComponent,
        FinancialBalanceWidgetComponent,
        PerformedWorkWidgetComponent,
        WorkedHoursWidgetComponent,
        WorkersWorkedHoursWidgetComponent,
        LowMedicationWidgetComponent,
        WorkSanctionDashboardWidgetComponent,
        WorkServiceAnnouncementDashboardWidgetComponent,
        CelebrationsWidgetComponent,
        MailRecordsWidgetComponent,
        NearEndVouchersWidgetComponent,
        RiskyClientsWidgetComponent,
        WorkMovementRestrictionWidgetComponent,
        WorkSuggestionDashboardWidgetComponent,
        SocialServiceIndividualPlanWidgetComponent,
        SocialServiceUserTasksWidgetComponent,
        UserMessagesWidgetComponent,
        NursingAllowanceDurationWidgetComponent,
        NursingAllowanceConfirmationWidgetComponent,
    ],
})
export class DashboardPageComponent {
    public readonly store = inject(DashboardPageStore);
    public readonly translate = inject(TranslateService);

    protected options: Safe = gridsterOptions;
    protected prefix = 'enum.serviceDashboardWidgets.';

    protected items = signal<Record<string, DashboardGridsterItem> | null>(null);
    private itemChangeSignal = signal<any>(null);

    private riskyClients: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.RISKY_CLIENTS,
        ...tightBasicGridsterItem,
    };

    private lowMedication: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.LOW_MEDICATION,
        ...smallBasicGridsterItem,
    };

    private nearEndVouchers: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS,
        ...smallBasicGridsterItem,
    };

    private celebrations: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.CELEBRATIONS,
        ...smallBasicGridsterItem,
    };

    private mailRecords: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.MAIL_RECORDS,
        ...smallBasicGridsterItem,
    };

    private errorsInFolder: DashboardGridsterItem = {
        name: 'errorsInFolder',
        ...smallBasicGridsterItem,
    };

    private movementRestrictions: DashboardGridsterItem = {
        name: 'movementRestrictions',
        ...smallBasicGridsterItem,
    };

    private sanction: DashboardGridsterItem = {
        name: 'sanction',
        ...smallBasicGridsterItem,
    };

    private individualPlan: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.INDIVIDUAL_PLAN,
        ...smallBasicGridsterItem,
    };

    private userTasks: DashboardGridsterItem = {
        name: 'userTasks',
        ...smallBasicGridsterItem,
    };

    private suggestion: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.SUGGESTION,
        ...smallBasicGridsterItem,
    };

    private userMessages: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.USER_MESSAGES,
        ...tightBasicGridsterItem,
    };

    private serviceAnnouncements: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS,
        ...smallBasicGridsterItem,
    };

    private clientsWithoutRecentWork: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.CLIENTS_WITHOUT_RECENT_WORK,
        ...basicGridsterItem,
    };

    private plannedWork: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.PLANNED_WORK,
        ...basicGridsterItem,
        cols: 20,
    };

    private debtors: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.DEBTORS,
        ...basicGridsterItem,
    };

    private operationalReports: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.OPERATIONAL_REPORTS,
        ...basicGridsterItem,
    };

    private financialBalance: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.FINANCIAL_BALANCE,
        ...basicGridsterItem,
        cols: 12,
        rows: 7,
    };

    private performedWork: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.PERFORMED_WORK,
        ...basicGridsterItem,
        rows: 7,
    };

    private workedHours: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.WORKED_HOURS,
        ...basicGridsterItem,
        cols: 8,
        rows: 6,
    };

    private workersWorkedHours: DashboardGridsterItem = {
        name: ServicesDashboardWidgetsEnum.WORKERS_WORKED_HOURS,
        ...basicGridsterItem,
        cols: 8,
        rows: 6,
    };

    private default_items = {
        'errorsInFolder': this.errorsInFolder,
        [ServicesDashboardWidgetsEnum.RISKY_CLIENTS]: this.riskyClients,
        [ServicesDashboardWidgetsEnum.LOW_MEDICATION]: this.lowMedication,
        [ServicesDashboardWidgetsEnum.CELEBRATIONS]: this.celebrations,
        [ServicesDashboardWidgetsEnum.MAIL_RECORDS]: this.mailRecords,
        [ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS]: this.nearEndVouchers,
        [ServicesDashboardWidgetsEnum.MOVEMENT_RESTRICTIONS]: this.movementRestrictions,
        [ServicesDashboardWidgetsEnum.SANCTION]: this.sanction,
        [ServicesDashboardWidgetsEnum.INDIVIDUAL_PLAN]: this.individualPlan,
        [ServicesDashboardWidgetsEnum.USER_TASKS]: this.userTasks,
        [ServicesDashboardWidgetsEnum.SUGGESTION]: this.suggestion,
        [ServicesDashboardWidgetsEnum.USER_MESSAGES]: this.userMessages,
        [ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS]: this.serviceAnnouncements,
        [ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_DURATION]: {
            name: ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_DURATION,
            ...smallBasicGridsterItem,
        },
        [ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_CONFIRMATION]: {
            name: ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_CONFIRMATION,
            ...smallBasicGridsterItem,
        },
        ...(featureOn.SOCIAL_SERVICE_DASHBOARD ? {
            [ServicesDashboardWidgetsEnum.CLIENTS_WITHOUT_RECENT_WORK]: this.clientsWithoutRecentWork,
            [ServicesDashboardWidgetsEnum.PLANNED_WORK]: this.plannedWork,
            [ServicesDashboardWidgetsEnum.DEBTORS]: this.debtors,
            [ServicesDashboardWidgetsEnum.OPERATIONAL_REPORTS]: this.operationalReports,
            [ServicesDashboardWidgetsEnum.FINANCIAL_BALANCE]: this.financialBalance,
            [ServicesDashboardWidgetsEnum.PERFORMED_WORK]: this.performedWork,
            [ServicesDashboardWidgetsEnum.WORKED_HOURS]: this.workedHours,
            [ServicesDashboardWidgetsEnum.WORKERS_WORKED_HOURS]: this.workersWorkedHours,
        } : {}),
    };

    constructor() {
        effect(() => {
            const userId = this.store.currentUserId;
            const services = this.store.services();
            const widgetsData = this.store.widgetsData();

            if (userId && services) {
                this.store.api.getDashboardSettings(FilterBuilder.singlePage({
                    key: SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY,
                    userId,
                })).then(async (settings: DashboardSettingsGridObject) => {
                    const item: DashboardSettingsObject = settings.items[0];
                    const data = item
                        ? JSON.parse(item.data) as Record<string, Partial<DashboardGridsterItem>>
                        : undefined;

                    const hasSocial = services.some(it => it.type === ServiceTypeEnum.SOCIAL);
                    const hasHealthCare = services.some(it => it.type === ServiceTypeEnum.HEALTH_CARE);

                    const def = { ...this.default_items };
                    if (!this.store.canShowErrorsInFolder()) {
                        delete (def as Record<string, DashboardGridsterItem>)['errorsInFolder'];
                    }
                    if (!hasHealthCare) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.RISKY_CLIENTS];
                        delete (def as any)[ServicesDashboardWidgetsEnum.LOW_MEDICATION];
                        delete (def as any)[ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS];
                    }
                    if (!hasSocial) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.MOVEMENT_RESTRICTIONS];
                        delete (def as any)[ServicesDashboardWidgetsEnum.SANCTION];
                        delete (def as any)[ServicesDashboardWidgetsEnum.SUGGESTION];
                        delete (def as any)[ServicesDashboardWidgetsEnum.MAIL_RECORDS];
                        delete (def as any)[ServicesDashboardWidgetsEnum.USER_MESSAGES];
                        delete (def as any)[ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS];
                    } else {
                        if (!widgetsData[ServicesDashboardWidgetsEnum.MOVEMENT_RESTRICTIONS]) {
                            delete (def as any)[ServicesDashboardWidgetsEnum.MOVEMENT_RESTRICTIONS];
                        }
                        if (!widgetsData[ServicesDashboardWidgetsEnum.SANCTION]) {
                            delete (def as any)[ServicesDashboardWidgetsEnum.SANCTION];
                        }
                        if (!widgetsData[ServicesDashboardWidgetsEnum.SUGGESTION]) {
                            delete (def as any)[ServicesDashboardWidgetsEnum.SUGGESTION];
                        }
                    }

                    if (featureOn.SOCIAL_SERVICE_DASHBOARD) {
                        Object.values(ServicesDashboardWidgetsEnum).forEach(widgetName => {
                            if (!widgetsData[widgetName]?.length) {
                                delete (def as Record<string, DashboardGridsterItem>)[widgetName];
                            }
                        });

                        const clientsWithoutRecentWorkEntries = widgetsData[
                            ServicesDashboardWidgetsEnum.CLIENTS_WITHOUT_RECENT_WORK
                        ];
                        const clientsWithoutRecentWorkItem = (def as Record<string, DashboardGridsterItem>)[
                            ServicesDashboardWidgetsEnum.CLIENTS_WITHOUT_RECENT_WORK
                        ];
                        if (clientsWithoutRecentWorkEntries?.length && clientsWithoutRecentWorkItem) {
                            clientsWithoutRecentWorkItem.headerTitle = clientsWithoutRecentWorkEntries.length === 1
                                ? this.translate.instant('dashboard.clientsWithoutRecentWork.headerTitle', {
                                    count: clientsWithoutRecentWorkEntries[0].widget.days,
                                })
                                : this.translate.instant('dashboard.clientsWithoutRecentWork.headerTitleMulti');
                        }
                    }

                    if (!featureOn.SOCIAL_JANUARY) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.MAIL_RECORDS];
                    }

                    if (!featureOn.SUGGESTION) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.SUGGESTION];
                    }

                    if (!featureOn.USER_MESSAGES) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.USER_MESSAGES];
                    }

                    if (!featureOn.SERVICE_ANNOUNCEMENT || !widgetsData[ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS]) {
                        delete (def as any)[ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS];
                    }

                    if (hasHealthCare) {
                        const nearEndVoucherEntries = widgetsData[ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS];
                        const hideNearEndVouchers = !['902', '913', '925'].some(x => services.map(x => x.expertise?.code).filter(x => !!x).includes(x))
                            || (featureOn.SOCIAL_SERVICE_DASHBOARD && !nearEndVoucherEntries?.length);
                        if (hideNearEndVouchers) {
                            delete (def as any)[ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS];
                        } else {
                            let title = undefined;
                            if (nearEndVoucherEntries?.length === 1) {
                                const daysCount = nearEndVoucherEntries[0].widget.days;
                                title = this.translate.instant('dashboard.nearEndVouchers.headerTitle', {
                                    count: daysCount,
                                });
                            } else {
                                title = this.translate.instant('dashboard.nearEndVouchers.headerTitleMulti');
                            }

                            const nearEndVoucherItem = (def as Record<string, DashboardGridsterItem>)[ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS];
                            if (nearEndVoucherItem) {
                                nearEndVoucherItem.headerTitle = title;
                            }
                        }
                    }

                    const dashboardItems = def as Record<string, DashboardGridsterItem>;
                    const hiddenWidgetNames = this.store.hiddenWidgetNames()
                        .filter(widgetName => !dashboardItems[widgetName]);
                    const layout = data
                        ? removeDashboardItemsFromLayout(data, hiddenWidgetNames)
                        : undefined;
                    const preparedItems = prepareWidgetDataToView(dashboardItems, layout);

                    try {
                        if (data && hiddenWidgetNames.some(widgetName => widgetName in data)) {
                            await this.saveDashboardSettings(layout, SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY);
                        }
                    } finally {
                        this.items.set(preparedItems);
                    }
                });
            }
        });

        effect(() => {
            const event = this.itemChangeSignal();
            if (event) {
                untracked(() => this.handleItemChanged(event));
            }
        });
    }

    itemChanged(event: any): void {
        this.itemChangeSignal.set(event);
    }

    handleItemChanged(event: any): void {
        const { item } = event;

        void this.saveDashboardSettings(this.items(), SERVICE_DASHBOARD_WIDGET_SETTINGS_KEY);
        if (this.items() !== null) {
            this.items()![item.name] = item as unknown as DashboardGridsterItem;
        }
    }

    private async saveDashboardSettings(data: any, key: string): Promise<void> {
        if (this.store.currentUserId) {
            await this.store.api.updateDashboardSettings({
                serviceId: null,
                userId: this.store.currentUserId || '',
                data: JSON.stringify(prepareDataToSave(data, key)),
                key,
            });
        }
    }

    protected readonly ServicesDashboardWidgetsEnum = ServicesDashboardWidgetsEnum;
}
